import React, { useState, useEffect, useCallback } from 'react';
import { getReports, getManagers, getCurrentUser } from '../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { format, subDays, startOfMonth } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  Calendar as CalendarIcon,
  TrendingUp,
  TrendingDown,
  Wallet,
  Users,
  Loader2,
  Search,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export default function ReportsPage() {
  const [managers, setManagers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Filters
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(new Date());
  const [selectedManager, setSelectedManager] = useState('all');

  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';
  const isTeamLeader = currentUser?.role === 'team_leader';

  const fetchManagers = useCallback(async () => {
    try {
      const data = await getManagers();
      setManagers(data);
    } catch (err) {
      console.error('Failed to fetch managers:', err);
    }
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);
      // For manager, auto-select themselves
      if (user.role === 'manager' && user.manager_id) {
        setSelectedManager(user.manager_id);
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
    fetchManagers();
  }, [fetchCurrentUser, fetchManagers]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const data = await getReports(
        format(startDate, 'yyyy-MM-dd'),
        format(endDate, 'yyyy-MM-dd'),
        selectedManager !== 'all' ? selectedManager : null
      );
      setReport(data);
    } catch (err) {
      console.error('Failed to fetch report:', err);
      toast.error('Ошибка загрузки отчёта');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value) + ' $';
  };

  // Quick date presets
  const setPreset = (preset) => {
    const today = new Date();
    switch (preset) {
      case 'week':
        setStartDate(subDays(today, 7));
        setEndDate(today);
        break;
      case 'month':
        setStartDate(startOfMonth(today));
        setEndDate(today);
        break;
      case '30days':
        setStartDate(subDays(today, 30));
        setEndDate(today);
        break;
      default:
        break;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Отчёты</h1>
        <p className="text-muted-foreground">Анализ доходов и расходов</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Quick presets */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreset('week')}
                data-testid="preset-week"
              >
                Неделя
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreset('month')}
                data-testid="preset-month"
              >
                Этот месяц
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreset('30days')}
                data-testid="preset-30days"
              >
                30 дней
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Start Date */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Дата с</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="report-start-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(startDate, 'PPP', { locale: ru })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(d) => d && setStartDate(d)}
                      locale={ru}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* End Date */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Дата по</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="report-end-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(endDate, 'PPP', { locale: ru })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(d) => d && setEndDate(d)}
                      disabled={(date) => date < startDate}
                      locale={ru}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Manager - hide for manager role (they only see themselves) */}
              {!isManager && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Менеджер</label>
                  <Select value={selectedManager} onValueChange={setSelectedManager}>
                    <SelectTrigger data-testid="report-manager-select">
                      <SelectValue placeholder="Все" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все</SelectItem>
                      {managers.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Generate Button */}
              <div className="flex items-end">
                <Button
                  className="w-full"
                  onClick={fetchReport}
                  disabled={loading}
                  data-testid="generate-report-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  Сформировать
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {report && (
        <div className="space-y-6 animate-fade-in">
          {/* Team Leader: Show separated income cards */}
          {isTeamLeader && report.team_leader_summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Team Income */}
              <Card className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Доход команды</p>
                    <p className="text-lg font-semibold income-text">
                      {formatCurrency(report.team_leader_summary.team_income)}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Team Commission */}
              <Card className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                    <Wallet className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Комиссия ({report.team_leader_summary.team_commission_percent}%)
                    </p>
                    <p className="text-lg font-semibold">
                      {formatCurrency(report.team_leader_summary.team_commission)}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Personal Income */}
              <Card className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                    <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Личный доход</p>
                    <p className="text-lg font-semibold income-text">
                      {formatCurrency(report.team_leader_summary.personal_income)}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Personal Salary */}
              <Card className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                    <Wallet className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Личная зарплата ({report.team_leader_summary.personal_percent}%)
                    </p>
                    <p className="text-lg font-semibold">
                      {formatCurrency(report.team_leader_summary.personal_salary)}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Team Leader: Total salary summary */}
          {isTeamLeader && report.team_leader_summary && (
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Итого к выплате</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(report.total_salaries)}
                    </p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <p>Комиссия: {formatCurrency(report.team_leader_summary.team_commission)}</p>
                    <p>Зарплата: {formatCurrency(report.team_leader_summary.personal_salary)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary Cards - for admin and manager (not team leader) */}
          {!isTeamLeader && (
            <div className={cn(
              "grid gap-4",
              isAdmin ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2"
            )}>
              <Card className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                    <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Доход</p>
                    <p className="text-lg font-semibold income-text">
                      {formatCurrency(report.total_income)}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Expense - admin only */}
              {isAdmin && (
                <Card className="stat-card">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                      <TrendingDown className="h-5 w-5 text-red-500 dark:text-red-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Расход</p>
                      <p className="text-lg font-semibold expense-text">
                        {formatCurrency(report.total_expense)}
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              <Card className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                    <Users className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Зарплаты</p>
                    <p className="text-lg font-semibold">{formatCurrency(report.total_salaries)}</p>
                  </div>
                </div>
              </Card>

              {/* Profit - admin only */}
              {isAdmin && (
                <Card className="stat-card">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'p-2 rounded-lg',
                        report.profit >= 0
                          ? 'bg-emerald-100 dark:bg-emerald-900/30'
                          : 'bg-red-100 dark:bg-red-900/30'
                      )}
                    >
                      <Wallet
                        className={cn(
                          'h-5 w-5',
                          report.profit >= 0
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-red-500 dark:text-red-400'
                        )}
                      />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Прибыль</p>
                      <p
                        className={cn(
                          'text-lg font-semibold',
                          report.profit >= 0 ? 'profit-positive' : 'profit-negative'
                        )}
                      >
                        {report.profit >= 0 ? '+' : ''}
                        {formatCurrency(report.profit)}
                      </p>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Manager Table */}
          {report.by_manager && report.by_manager.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>По менеджерам</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Менеджер</TableHead>
                      <TableHead className="text-right">Доход</TableHead>
                      <TableHead className="text-right">Сделки</TableHead>
                      <TableHead className="text-right">Зарплата</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.by_manager.map((row, idx) => (
                      <TableRow key={idx} data-testid={`report-manager-row-${idx}`}>
                        <TableCell className="font-medium">{row.manager_name}</TableCell>
                        <TableCell className="text-right income-text">
                          {formatCurrency(row.total_income)}
                        </TableCell>
                        <TableCell className="text-right">{row.deals_count}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.salary)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Empty State */}
      {!report && !loading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Выберите период</h3>
            <p className="text-muted-foreground">
              Укажите даты и нажмите "Сформировать" для получения отчёта
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
